export interface MdToken {
  type: string
  text?: string
  depth?: number
  lang?: string
  ordered?: boolean
  start?: number | string
  href?: string
  tokens?: MdToken[]
  items?: MdToken[]
  header?: MdToken[]
  rows?: MdToken[][]
  align?: Array<'left' | 'right' | 'center' | null>
}

interface RawToken {
  type?: string
  text?: unknown
  depth?: unknown
  lang?: unknown
  ordered?: unknown
  start?: unknown
  href?: unknown
  tokens?: unknown
  items?: unknown
  header?: unknown
  rows?: unknown
  align?: unknown
}

export interface FrontMatter {
  title: string
  slug: string
  order: number
  description: string
}

const PLACEHOLDER = /\{\{(merchantApi|terminal|sdkScript)\}\}/

function unsafePath(href: string): boolean {
  if (href.includes('\\')) return true
  for (const char of href) {
    if (char.trim() === '') return true
    const code = char.codePointAt(0) ?? 0
    if (code < 32 || code === 127) return true
  }
  return false
}

export function splitFrontMatter(source: string): {
  matter: FrontMatter
  body: string
} {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n/.exec(source)
  if (!match) throw new Error('page is missing front matter')
  const fields = new Map<string, string>()
  for (const line of match[1].split('\n')) {
    const index = line.indexOf(':')
    if (index === -1) continue
    fields.set(line.slice(0, index).trim(), line.slice(index + 1).trim())
  }
  const title = fields.get('title')
  const slug = fields.get('slug')
  const order = Number(fields.get('order'))
  if (!title || !slug || !Number.isInteger(order)) {
    throw new Error('page front matter needs title, slug, and order')
  }
  return {
    matter: {
      title,
      slug,
      order,
      description: fields.get('description') ?? ''
    },
    body: source.slice(match[0].length)
  }
}

function tokenList(value: unknown): RawToken[] | undefined {
  if (!Array.isArray(value)) return undefined
  return value.filter(
    (item): item is RawToken => typeof item === 'object' && item !== null
  )
}

function rowList(value: unknown): RawToken[][] | undefined {
  if (!Array.isArray(value)) return undefined
  const rows: RawToken[][] = []
  for (const row of value) {
    const cells = tokenList(row)
    if (cells) rows.push(cells)
  }
  return rows
}

function alignOf(value: unknown): MdToken['align'] | undefined {
  if (!Array.isArray(value)) return undefined
  const align: Array<'left' | 'right' | 'center' | null> = []
  for (const item of value) {
    if (
      item === 'left' ||
      item === 'right' ||
      item === 'center' ||
      item === null
    ) {
      align.push(item)
    }
  }
  return align
}

export function plainToken(token: RawToken): MdToken {
  const plain: MdToken = { type: token.type ?? 'text' }
  if (typeof token.text === 'string') plain.text = token.text
  if (typeof token.depth === 'number') plain.depth = token.depth
  if (typeof token.lang === 'string') plain.lang = token.lang
  if (typeof token.ordered === 'boolean') plain.ordered = token.ordered
  if (typeof token.start === 'number') plain.start = token.start
  if (typeof token.href === 'string') plain.href = token.href
  const tokens = tokenList(token.tokens)
  if (tokens) plain.tokens = tokens.map(plainToken)
  const items = tokenList(token.items)
  if (items) plain.items = items.map(plainToken)
  // A table cell uses `header` as a boolean. Only a table token has an array.
  const header = tokenList(token.header)
  if (header) plain.header = header.map(plainToken)
  const rows = rowList(token.rows)
  if (rows) plain.rows = rows.map(row => row.map(plainToken))
  const align = alignOf(token.align)
  if (align) plain.align = align
  return plain
}

export function hasPlaceholder(token: MdToken): boolean {
  if (typeof token.text === 'string' && PLACEHOLDER.test(token.text))
    return true
  if (typeof token.href === 'string' && PLACEHOLDER.test(token.href))
    return true
  if (token.tokens?.some(hasPlaceholder)) return true
  if (token.items?.some(hasPlaceholder)) return true
  if (token.header?.some(hasPlaceholder)) return true
  return token.rows?.some(row => row.some(hasPlaceholder)) ?? false
}

export function safeHref(href: string): string | null {
  if (href.startsWith('/') && !href.startsWith('//') && !unsafePath(href)) {
    return href
  }
  try {
    const url = new URL(href)
    if (url.protocol === 'https:' || url.protocol === 'http:') return href
  } catch {
    return null
  }
  return null
}
